import { Badge, Collapse, Divider, Group, NavLink, Stack, Text, Tooltip } from "@mantine/core"
import { type ElementType, Fragment, type ReactNode } from "react"

// Inline chevron so shared-ui stays icon-library-free (consumers inject their own
// icons via AppSidebarSection.icon). Rotates 90° when its section is open/active.
const Chevron = ({ open }: { readonly open: boolean }) => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 150ms ease" }}
    aria-hidden
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
)

/**
 * Top-level navigation entry. Top-level entries get an icon and may have
 * children that render as indented sub-links beneath them in expanded
 * mode (children are suppressed in collapsed/rail mode — clicking the
 * top-level item navigates to `path` and the destination page is
 * responsible for surfacing sub-nav).
 */
export type AppSidebarSection = {
  readonly id: string
  readonly label: string
  readonly icon: ElementType
  /** Where clicking the section navigates. */
  readonly path: string
  /**
   * Path prefix used for active-state matching. Defaults to `path`. Set this
   * when `path` is a deep link (e.g. nexus modes navigate to their first
   * panel) but the section should highlight for the whole subtree.
   */
  readonly matchPath?: string
  /**
   * Optional small status tag (e.g. "Demo") rendered as a subtle pill on the section
   * header — the top-level counterpart of `AppSidebarChild.tag`. Domain-neutral: the
   * consumer supplies the word. Suppressed in collapsed/rail mode, where there is no
   * room for it.
   */
  readonly tag?: string
  readonly children?: ReadonlyArray<AppSidebarChild>
}

export type AppSidebarChild = {
  readonly id: string
  readonly label: string
  readonly path: string
  /** Optional count/label badge rendered at the right edge of the child link. */
  readonly badge?: number | string
  /** Optional small status tag (e.g. "Demo") rendered as a subtle gray pill at the right edge,
   *  left of any count `badge`. Domain-neutral — the consumer supplies the word. */
  readonly tag?: string
  /**
   * Optional cluster label. When a child's `group` differs from the previous
   * child's, a small non-clickable group header is rendered above it. Children
   * sharing a group must be contiguous (the header renders on change). Omit to
   * keep a flat list.
   */
  readonly group?: string
}

export type AppSidebarProps = {
  /** Top-level sections rendered in order. */
  readonly sections: ReadonlyArray<AppSidebarSection>
  /** When true, sidebar is in slim-icon-rail mode (~56px). */
  readonly collapsed: boolean
  /** Current router pathname — used to highlight the active section/child. */
  readonly activePath: string
  /** Called when a section or child link is clicked. */
  readonly onNavigate: (path: string) => void
  /** Optional content rendered at the very top of the navbar (brand area). */
  readonly brand?: ReactNode
  /**
   * Accordion mode: render a section's children only when it is the active
   * section; inactive sections collapse to their header (with a chevron
   * affordance). "Open" == "active" — clicking an inactive header navigates to
   * its `path`, which makes it active and expands it. Default `false` keeps the
   * legacy behavior (all sections' children always shown) for other consumers.
   */
  readonly accordion?: boolean
}

/**
 * AppSidebar — the Civala chrome sidebar.
 *
 * Renders inside a Mantine `AppShell.Navbar`. Styling comes from
 * `pitch-global.css` `.civala-chrome-pitch` rules (light-on-light in
 * light mode, dark in dark mode) — wrap `AppShell` with that class.
 *
 * Collapse / expand state is owned by the parent. Sidebar is purely
 * presentational; it does not own the disclosure state or persist it.
 */
export function AppSidebar({ sections, collapsed, activePath, onNavigate, brand, accordion = false }: AppSidebarProps) {
  return (
    <>
      {brand}
      <Stack gap={0} pt="xs">
        {sections.map((section, i) => {
          const Icon = section.icon
          const matchTarget = section.matchPath ?? section.path
          const isActive = matchTarget === "/" ? activePath === "/" : activePath.startsWith(matchTarget)
          const hasChildren = (section.children?.length ?? 0) > 0
          // Accordion: only the active section reveals its children; inactive ones stay headers-only.
          // (`open` drives the Collapse height animation; rail/collapsed mode drops children entirely.)
          const open = !accordion || isActive
          // Disclosure chevron only in accordion + expanded mode for sections that have children.
          const showChevron = accordion && !collapsed && hasChildren

          // Tag and chevron can coexist (a tagged section may also have children), so the
          // right edge holds whichever of the two apply.
          const tagBadge =
            section.tag != null && !collapsed ? (
              <Badge size="xs" variant="light" color="gray">
                {section.tag}
              </Badge>
            ) : undefined
          const rightSection =
            tagBadge != null || showChevron ? (
              <Group gap={6} wrap="nowrap">
                {tagBadge}
                {showChevron && <Chevron open={isActive} />}
              </Group>
            ) : undefined

          const link = (
            <NavLink
              label={collapsed ? undefined : section.label}
              leftSection={<Icon size={18} stroke={1.5} />}
              rightSection={rightSection}
              active={isActive}
              onClick={() => onNavigate(section.path)}
              style={{ fontWeight: 600, fontSize: 13 }}
            />
          )

          return (
            <div key={section.id}>
              {i > 0 && <Divider my={4} mx="sm" />}
              {collapsed ? (
                <Tooltip label={section.label} position="right" withArrow openDelay={200}>
                  {link}
                </Tooltip>
              ) : (
                link
              )}
              {/* Children animate height on expand/collapse (Mantine Collapse). Rail mode drops
                  them entirely; non-accordion consumers keep them always-open (in=true → no anim).
                  `key` = the child-id signature: Mantine's Collapse measures content height only on
                  the expanded-toggle, not when children change while it stays open — so if the child
                  set changes (e.g. a demo-mode filter adds/removes panels), the height goes stale
                  (clipped or gappy). Re-keying remounts the Collapse to re-measure. Normal navigation
                  doesn't change a section's children, so its animation is untouched. */}
              {!collapsed && hasChildren && (
                <Collapse
                  key={(section.children ?? []).map((c) => c.id).join("|")}
                  expanded={open}
                  transitionDuration={200}
                >
                  {(section.children ?? []).map((child, ci, arr) => {
                    // Render a small cluster header when this child starts a new group.
                    const showGroup = child.group != null && child.group !== arr[ci - 1]?.group
                    return (
                      <Fragment key={child.id}>
                        {showGroup && (
                          <Text
                            size="xs"
                            fw={700}
                            tt="uppercase"
                            c="dimmed"
                            pl={16}
                            pt={ci === 0 ? 4 : 10}
                            pb={2}
                            style={{ letterSpacing: "0.06em", fontSize: 10 }}
                          >
                            {child.group}
                          </Text>
                        )}
                        <NavLink
                          label={
                            child.tag != null || child.badge != null ? (
                              <Group justify="space-between" wrap="nowrap" gap="xs">
                                <Text size="sm" truncate>
                                  {child.label}
                                </Text>
                                <Group gap={6} wrap="nowrap">
                                  {child.tag != null && (
                                    <Badge size="xs" variant="light" color="gray">
                                      {child.tag}
                                    </Badge>
                                  )}
                                  {child.badge != null && (
                                    <Badge size="xs" variant="filled" color="pitchNegative" circle>
                                      {child.badge}
                                    </Badge>
                                  )}
                                </Group>
                              </Group>
                            ) : (
                              child.label
                            )
                          }
                          active={activePath === child.path}
                          onClick={() => onNavigate(child.path)}
                          pl={36}
                          py={5}
                        />
                      </Fragment>
                    )
                  })}
                </Collapse>
              )}
            </div>
          )
        })}
      </Stack>
    </>
  )
}
